/**
 * File sizes for humans — ported from v1's `monza.endpoint.formatFileSize` (upload.js).
 *
 * ★★ DELIBERATELY THE SAME ALGORITHM AS v1, not a re-invention. Suppliers have been reading these
 * numbers for years in the old portal ("Shalborne HD Stereo.mp4 · 844.69 Mb"), and a delivery they
 * remember as 844.69 Mb should not silently become "845 MB" in the new one. Same base, same unit
 * names, same rounding — so a v1 screenshot and a v2 screen agree.
 *
 * The details that make it v1's, all of which look arbitrary until you compare outputs:
 *   · BINARY units (1024), but labelled `Kb`/`Mb`/`Gb` — not the strict `KiB`. v1 chose the
 *     familiar labels over the pedantic ones and so do we.
 *   · TWO decimals, which is what makes a multi-GB master legible: "4.72 Gb" says something
 *     "5 Gb" does not, and these are files people wait hours for.
 *   · `parseFloat` on the fixed string, so trailing zeros are dropped — `2.00 Mb` prints as
 *     `2 Mb`. That is why the numbers never look mechanical.
 *
 * ⚠ Shared by BOTH portals (provider + admin) and matched by the PHP notifier, so one delivery
 * reads identically in the transfer list, the admin screen and the email. Three formatters would
 * drift, and someone would eventually query why the same file has two sizes.
 */

const BINARY_UNITS = ['Bytes', 'Kb', 'Mb', 'Gb', 'Tb', 'Pb', 'Eb', 'Zb', 'Yb'] as const
const DECIMAL_UNITS = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'] as const

export function formatFileSize(bytes: number, decimals = 2, binaryUnits = true): string {
  // ⚠ v1 returned '0 Bytes' for zero and would produce `NaN`/`-Infinity` for anything odd. We keep
  // its output for every real value and simply refuse to print nonsense for the rest.
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 Bytes'

  const unitMultiple = binaryUnits ? 1024 : 1000
  const unitNames = binaryUnits ? BINARY_UNITS : DECIMAL_UNITS

  const unitChanges = Math.min(
    Math.floor(Math.log(bytes) / Math.log(unitMultiple)),
    unitNames.length - 1,
  )

  // parseFloat trims the trailing zeros toFixed leaves behind — v1's trick, and the reason
  // "2 Mb" reads naturally where "2.00 Mb" would not.
  const value = parseFloat((bytes / unitMultiple ** unitChanges).toFixed(decimals || 0))

  return `${value} ${unitNames[unitChanges]}`
}
